import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import hoteltemplateService from '../services/hoteltemplateService';
import canonicalgroupService from '../services/canonicalgroupService';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import vaultsourceService from '../services/vaultsourceService';
import vaultsqlService from '../services/vaultsqlService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import ConfirmModal from './shared/ConfirmModal';
import TemplateAssistantChat from './TemplateAssistantChat';
import './AccountForm.css';

const VAULT_STANDARD_FIELDS = [
  'invoiceNo', 'folioNo', 'invoiceDate', 'billingName', 'guestName',
  'departureDate', 'arrivalDate', 'email', 'confirmationNumber',
  'invoiceAmount', 'membershipNumber', 'externalReference', 'arNo',
  'year', 'month'
];

const HOTEL_FIELDS = [
  'hotel.logo', 'hotel.hotelname', 'hotel.hotelcode',
  'hotel.ADDRESS1', 'hotel.ADDRESS2', 'hotel.CITY', 'hotel.STATE', 'hotel.ZIPCODE',
  'hotel.PHONE', 'hotel.FAX', 'hotel.WEBSITE',
  'hotel.COUNTRY', 'hotel.CURRENCY'
];


function extractSqlColumns(sql) {
  if (!sql) return [];
  const match = sql.match(/SELECT\s+([\s\S]+?)\s+FROM\b/i);
  if (!match) return [];
  const colsPart = match[1];
  const cols = [];
  let depth = 0, current = '';
  for (const ch of colsPart) {
    if (ch === '(') { depth++; current += ch; }
    else if (ch === ')') { depth--; current += ch; }
    else if (ch === ',' && depth === 0) { cols.push(current.trim()); current = ''; }
    else { current += ch; }
  }
  if (current.trim()) cols.push(current.trim());
  return cols.map(col => {
    const asMatch = col.match(/\bAS\s+`?(\w+)`?\s*$/i);
    if (asMatch) return asMatch[1];
    const parts = col.trim().split(/[\s.]+/);
    return parts[parts.length - 1].replace(/[`'"]/g, '') || null;
  }).filter(c => c && c !== '*');
}

const HotelTemplates = ({ hotelId }) => {
  const navigate = useNavigate();
  const [hotelTemplates, setHotelTemplates] = useState([]);
  const [canonicalGroups, setCanonicalGroups] = useState([]);
  const [hotelRecord, setHotelRecord] = useState(null);
  const [accountLogo, setAccountLogo] = useState(null);
  const [templateForm, setTemplateForm] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateSqlFields, setTemplateSqlFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const activeTextareaRef = useRef(null);
  // FULL DOCUMENT TEMPLATES — the whole document for one vault system group, kept separate from the
  // header/footer rows above because they are keyed differently: header/footer by canonical group, a
  // full template by the vault system group whose query produced the row being rendered.
  const [fullTemplates, setFullTemplates] = useState([]);
  const [vaultSources, setVaultSources] = useState([]);

  useEffect(() => {
    if (!hotelId) return;
    fetchHotelTemplates();
    fetchFullTemplates();
    // Only the vault system NAMES are needed here, to label a row; the group list lives on the full
    // template screen, which is where one is chosen.
    vaultsourceService.getAllVaultSources()
      .then(r => setVaultSources(r.vaultsources || []))
      .catch(() => {});
    canonicalgroupService.getAllCanonicalGroups()
      .then(r => setCanonicalGroups(r.data?.canonicalgroups || []))
      .catch(() => {});
    hotelService.getHotelById(hotelId)
      .then(r => {
        const hotel = r.hotel || r;
        setHotelRecord(hotel || null);
        if (hotel?.accountid) {
          accountService.getAccountById(hotel.accountid)
            .then(acc => setAccountLogo(acc?.account?.logo ?? acc?.logo ?? null))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [hotelId]);

  useEffect(() => {
    if (!templateForm?.canonical_group_id) {
      setTemplateSqlFields([]);
      return;
    }
    const groupId = templateForm.canonical_group_id;
    (async () => {
      try {
        const groupRes = await canonicalgroupService.getCanonicalGroupById(groupId);
        const group = groupRes.data;
        if (group.name) {
          const vsgRes = await vaultsourcegroupService.getAllVaultSourceGroups({ name: group.name });
          const vsGroups = vsgRes.vaultsourcegroups || [];
          if (vsGroups.length > 0) {
            const sqlRes = await vaultsqlService.getAllVaultSqls({ vaultsourcegroupid: vsGroups[0].id });
            const sqls = sqlRes.vaultsqls || [];
            const allCols = sqls.flatMap(s => extractSqlColumns(s.sql_source));
            setTemplateSqlFields([...new Set(allCols)]);
          }
        }
      } catch (e) {
        console.error('[templateFields] Failed to load fields:', e);
      }
    })();
  }, [templateForm?.canonical_group_id]);

  const getCountryName = (code) => {
    if (!code) return '';
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
    } catch {
      return code;
    }
  };

  const substitutePreview = (html) => {
    if (!html) return '';
    const h = hotelRecord || {};
    const replacements = {
      'account.logo': accountLogo || '',
      'hotel.logo': h.logo || '',
      'hotel.hotelname': h.hotelname || h.HOTELNAME || 'Example Hotel Name',
      'hotel.hotelcode': h.hotelcode || h.HOTELCODE || '',
      'hotel.ADDRESS1': h.ADDRESS1 || 'Example Address',
      'hotel.ADDRESS2': h.ADDRESS2 || '',
      'hotel.CITY': h.CITY || 'Example City',
      'hotel.STATE': h.STATE || '',
      'hotel.ZIPCODE': h.ZIPCODE || 'Example Zip Code',
      'hotel.PHONE': h.PHONE || '',
      'hotel.FAX': h.FAX || '',
      'hotel.WEBSITE': h.WEBSITE || '',
      'hotel.COUNTRY': getCountryName(h.COUNTRY),
      'hotel.CURRENCY': h.CURRENCY || '',
    };
    return html.replace(/\{\{([\w.]+)\}\}/g, (_, key) =>
      key in replacements ? replacements[key] : `{{${key}}}`
    );
  };

  const fetchHotelTemplates = async () => {
    if (!hotelId) return;
    try {
      const res = await hoteltemplateService.getAll({ hotel_id: hotelId });
      setHotelTemplates(res.data?.hoteltemplates || []);
    } catch (err) {
      console.error('Failed to fetch hotel templates:', err);
    }
  };

  const fetchFullTemplates = async () => {
    if (!hotelId) return;
    try {
      const res = await hoteltemplateService.getAllFull({ hotel_id: hotelId });
      setFullTemplates(res.data?.hotelfulltemplates || []);
    } catch (err) {
      console.error('Failed to fetch hotel full templates:', err);
    }
  };

  const handleFullDelete = (template) => {
    setConfirmModal({
      title: 'Delete Full Template',
      message: `Delete the ${template.vault_source_group_name} full template for this hotel? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await hoteltemplateService.deleteFull(template.id);
          setFullTemplates(prev => prev.filter(t => t.id !== template.id));
        } catch (err) {
          console.error('Failed to delete full template:', err);
        }
      },
    });
  };

  const copyField = (key) => {
    const token = `{{${key}}}`;
    // Copy to the clipboard so it can be pasted into the HTML editor or the assistant chat.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(token).catch(() => {});
    } else {
      const ta = document.createElement('textarea');
      ta.value = token;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedField(key);
    setTimeout(() => setCopiedField(prev => (prev === key ? null : prev)), 1200);

    // Convenience: if a header/footer HTML box is the active element, also drop it at the cursor.
    const el = activeTextareaRef.current;
    if (el && document.activeElement === el && el.dataset && el.dataset.field) {
      const field = el.dataset.field;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newValue = el.value.substring(0, start) + token + el.value.substring(end);
      setTemplateForm(prev => ({ ...prev, [field]: newValue }));
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      }, 0);
    }
  };

  const handleTemplateSave = async () => {
    if (!templateForm) return;
    if (!templateForm.canonical_group_id) {
      setTemplateError('Document type is required');
      return;
    }
    setTemplateSaving(true);
    setTemplateError(null);
    try {
      if (templateForm.mode === 'create') {
        await hoteltemplateService.create({
          hotel_id: parseInt(hotelId),
          canonical_group_id: parseInt(templateForm.canonical_group_id),
          header: templateForm.header || null,
          footer: templateForm.footer || null,
        });
      } else {
        await hoteltemplateService.update(templateForm.id, {
          header: templateForm.header || null,
          footer: templateForm.footer || null,
        });
      }
      await fetchHotelTemplates();
      setTemplateForm(null);
    } catch (err) {
      setTemplateError(err.response?.data?.error?.message || err.message || 'Failed to save');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleTemplateDelete = (template) => {
    setConfirmModal({
      title: 'Delete Template',
      message: `Delete the ${template.canonical_group_name} template for this hotel? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await hoteltemplateService.delete(template.id);
          setHotelTemplates(prev => prev.filter(t => t.id !== template.id));
        } catch (err) {
          console.error('Failed to delete template:', err);
        }
      },
    });
  };

  // Template variables offered to the assistant (excludes the standard vault fields
  // that are substituted automatically at render time).
  const assistantFields = [
    'account.logo',
    ...HOTEL_FIELDS,
    ...templateSqlFields.filter(f => !VAULT_STANDARD_FIELDS.map(s => s.toLowerCase()).includes(f.toLowerCase())),
  ];

  if (!hotelId) return null;

  return (
    <div className="tab-panel">
      {templateForm ? (
        <div className="account-template-form">
          <h3>{templateForm.mode === 'create' ? 'New Template' : 'Edit Template'}</h3>
          {templateError && <div className="error-message">{templateError}</div>}

          {templateForm.mode === 'create' && (
            <div className="form-row">
              <div className="form-group">
                <label>Document Type *</label>
                <select
                  value={templateForm.canonical_group_id}
                  onChange={e => setTemplateForm(prev => ({ ...prev, canonical_group_id: e.target.value }))}
                >
                  <option value="">-- Select document type --</option>
                  {canonicalGroups.map(cg => (
                    <option key={cg.id} value={cg.id}>{cg.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {templateForm.mode === 'edit' && (
            <div className="account-template-meta">
              <span><strong>Document Type:</strong> {templateForm.canonical_group_name}</span>
            </div>
          )}

          <div className="account-template-editor-layout">
            {/* Fields panel */}
            <div className="template-payload-panel">
              <h4>Available Fields</h4>
              <input
                type="text"
                className="payload-field-search"
                placeholder="Search fields..."
                value={fieldSearch}
                onChange={e => setFieldSearch(e.target.value)}
              />
              <div className="payload-fields-list">
                {(() => {
                  const q = fieldSearch.toLowerCase();
                  const match = key => !q || key.toLowerCase().includes(q);

                  const accountFields = ['account.logo'].filter(match);
                  const hotelFields = HOTEL_FIELDS.filter(match);
                  const sqlFields = templateSqlFields
                    .filter(f => !VAULT_STANDARD_FIELDS.map(s => s.toLowerCase()).includes(f.toLowerCase()))
                    .filter(match);

                  return (
                    <>
                      {accountFields.length > 0 && (
                        <>
                          <div className="payload-fields-divider">Account</div>
                          <button
                            type="button"
                            className="payload-field-btn payload-field-btn--account"
                            onClick={() => copyField('account.logo')}
                            title="Copy {{account.logo}} to clipboard"
                          >
                            {copiedField === 'account.logo' ? '✓ Copied' : '{{account.logo}}'}
                            {accountLogo && <img src={accountLogo} alt="" style={{ maxHeight: 16, maxWidth: 40, marginLeft: 6, verticalAlign: 'middle', borderRadius: 2 }} />}
                          </button>
                        </>
                      )}
                      {hotelFields.length > 0 && (
                        <>
                          <div className="payload-fields-divider">Hotel</div>
                          {hotelFields.map(key => (
                            <button key={key} type="button" className="payload-field-btn payload-field-btn--hotel" onClick={() => copyField(key)} title={`Copy {{${key}}} to clipboard`}>
                              {copiedField === key ? '✓ Copied' : `{{${key}}}`}
                            </button>
                          ))}
                        </>
                      )}
                      {sqlFields.length > 0 && (
                        <>
                          <div className="payload-fields-divider">SQL Columns</div>
                          {sqlFields.map(key => (
                            <button key={key} type="button" className="payload-field-btn" onClick={() => copyField(key)} title={`Copy {{${key}}} to clipboard`}>
                              {copiedField === key ? '✓ Copied' : `{{${key}}}`}
                            </button>
                          ))}
                        </>
                      )}
                      {!accountFields.length && !hotelFields.length && !sqlFields.length && (
                        <p className="payload-hint" style={{ padding: '0.5rem 0' }}>No fields match</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Editors */}
            <div>
              <div className="form-group">
                <div className="template-label-row">
                  <label>Header HTML</label>
                  <button
                    type="button"
                    className="btn-example-template"
                    onClick={() => setTemplateForm(prev => ({ ...prev, header: `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;font-size:13px;color:#222;">
  <tr>
    <td style="padding:16px 24px;vertical-align:middle;">
      <img src="{{account.logo}}" alt="Logo" style="max-height:60px;max-width:200px;display:block;" />
    </td>
    <td style="padding:16px 24px;text-align:right;vertical-align:middle;">
      <strong>{{hotel.hotelname}}</strong><br/>
      {{hotel.ADDRESS1}}<br/>
      {{hotel.CITY}}, {{hotel.COUNTRY}}
    </td>
  </tr>
</table>` }))}
                    title="Insert example header with logo"
                  >
                    Example Header
                  </button>
                </div>
                <textarea
                  className="account-template-html"
                  data-field="header"
                  value={templateForm.header || ''}
                  onChange={e => setTemplateForm(prev => ({ ...prev, header: e.target.value }))}
                  onFocus={e => { activeTextareaRef.current = e.target; }}
                  placeholder="Paste header HTML here (e.g. logo, hotel name, address)..."
                  spellCheck={false}
                  rows={10}
                />
                {templateForm.header && (
                  <iframe
                    className="account-template-preview-iframe"
                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="referrer" content="no-referrer"></head><body style="margin:0;padding:8px;">${substitutePreview(templateForm.header)}</body></html>`}
                    title="Header Preview"
                    onLoad={e => { const f = e.target; if (f.contentDocument && f.contentDocument.body) f.style.height = f.contentDocument.body.scrollHeight + 'px'; }}
                  />
                )}
                <div style={{ marginTop: '0.75rem' }}>
                  <TemplateAssistantChat
                    target="header"
                    availableFields={assistantFields}
                    currentHeader={templateForm.header}
                    currentFooter={templateForm.footer}
                    onInsert={html => setTemplateForm(prev => ({ ...prev, header: html }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="template-label-row">
                  <label>Footer HTML</label>
                  <button
                    type="button"
                    className="btn-example-template"
                    onClick={() => setTemplateForm(prev => ({ ...prev, footer: `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;font-size:11px;color:#666;border-top:1px solid #e5e7eb;">
  <tr>
    <td style="padding:16px 24px;">
      <p style="margin:0 0 6px 0;">Thank you for staying with us. We hope you enjoyed your visit.</p>
      <p style="margin:0 0 6px 0;">Please review your charges carefully and contact the front desk if you have any questions.</p>
      <p style="margin:0;">We look forward to welcoming you again soon.</p>
    </td>
  </tr>
  <tr>
    <td style="padding:8px 24px;border-top:1px solid #e5e7eb;">
      {{hotel.hotelname}} &bull; {{hotel.ADDRESS1}}, {{hotel.CITY}}, {{hotel.COUNTRY}}
    </td>
  </tr>
</table>` }))}
                    title="Insert example footer"
                  >
                    Example Footer
                  </button>
                </div>
                <textarea
                  className="account-template-html"
                  data-field="footer"
                  value={templateForm.footer || ''}
                  onChange={e => setTemplateForm(prev => ({ ...prev, footer: e.target.value }))}
                  onFocus={e => { activeTextareaRef.current = e.target; }}
                  placeholder="Paste footer HTML here (e.g. terms, contact info)..."
                  spellCheck={false}
                  rows={10}
                />
                {templateForm.footer && (
                  <iframe
                    className="account-template-preview-iframe"
                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="referrer" content="no-referrer"></head><body style="margin:0;padding:8px;">${substitutePreview(templateForm.footer)}</body></html>`}
                    title="Footer Preview"
                    onLoad={e => { const f = e.target; if (f.contentDocument && f.contentDocument.body) f.style.height = f.contentDocument.body.scrollHeight + 'px'; }}
                  />
                )}
                <div style={{ marginTop: '0.75rem' }}>
                  <TemplateAssistantChat
                    target="footer"
                    availableFields={assistantFields}
                    currentHeader={templateForm.header}
                    currentFooter={templateForm.footer}
                    onInsert={html => setTemplateForm(prev => ({ ...prev, footer: html }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="btn-primary" onClick={handleTemplateSave} disabled={templateSaving}>
              {templateSaving ? 'Saving...' : templateForm.mode === 'create' ? 'Create Template' : 'Update Template'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setTemplateForm(null); setTemplateError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="hotels-header with-title">
            {/* The tab is "Templates", which does not say what these rows are. Each one is the header and
                footer this hotel puts on a document type — the parts around the folio, not the folio
                template itself, which lives on the canonical group. */}
            <h3 className="section-heading">Header &amp; Footer</h3>
            <button
              type="button"
              className="btn-add-hotel"
              onClick={() => {
                setTemplateError(null);
                setTemplateForm({ mode: 'create', canonical_group_id: '', header: '', footer: '' });
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add Template
            </button>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>Header</th>
                  <th>Footer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {hotelTemplates.length === 0 ? (
                  <tr><td colSpan="4" className="no-data">
                    <p>No templates defined for this hotel</p>
                    <p className="no-data-hint">Add a template to define headers and footers per document type</p>
                  </td></tr>
                ) : (
                  hotelTemplates.map(t => (
                    <tr
                      key={t.id}
                      className="clickable-row"
                      onClick={() => { setTemplateError(null); setTemplateForm({ mode: 'edit', id: t.id, canonical_group_id: t.canonical_group_id, canonical_group_name: t.canonical_group_name, header: t.header || '', footer: t.footer || '' }); }}
                    >
                      <td>{t.canonical_group_name}</td>
                      <td>{t.header ? <span className="template-has-content">✓</span> : <span className="template-no-content">—</span>}</td>
                      <td>{t.footer ? <span className="template-has-content">✓</span> : <span className="template-no-content">—</span>}</td>
                      <td className="actions-cell">
                        <button
                          type="button"
                          className="btn-action btn-edit-action"
                          onClick={e => { e.stopPropagation(); setTemplateError(null); setTemplateForm({ mode: 'edit', id: t.id, canonical_group_id: t.canonical_group_id, canonical_group_name: t.canonical_group_name, header: t.header || '', footer: t.footer || '' }); }}
                          title="Edit"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="btn-action btn-delete-action"
                          onClick={e => { e.stopPropagation(); handleTemplateDelete(t); }}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 4h10M6 4V2h4v2M5 4v9h6V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* FULL TEMPLATE — the whole document, not the parts around it. Chosen by VAULT SYSTEM GROUP
              (the list on /admin/vaults/<id>) because that is what a vault search result belongs to: the
              row came back from a group's query, so this is the template that renders it. */}
          <div className="hotels-header with-title" style={{ marginTop: '2rem' }}>
            <h3 className="section-heading">Full Template</h3>
            <button
              type="button"
              className="btn-add-hotel"
              onClick={() => navigate(`/hotel/${hotelId}/fulltemplate/new`)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add Full Template
            </button>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>System Group</th>
                  <th>Name</th>
                  <th>Template</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fullTemplates.length === 0 ? (
                  <tr><td colSpan="4" className="no-data">
                    <p>No full templates defined for this hotel</p>
                    <p className="no-data-hint">Add one to give a system group its own complete document layout</p>
                  </td></tr>
                ) : (
                  fullTemplates.map(t => (
                    <tr
                      key={t.id}
                      className="clickable-row"
                      onClick={() => navigate(`/hotel/${hotelId}/fulltemplate/${t.id}`)}
                    >
                      <td>{t.vault_source_name ? `${t.vault_source_name} — ${t.vault_source_group_name}` : t.vault_source_group_name}</td>
                      <td>{t.name || <span className="template-no-content">—</span>}</td>
                      <td>{t.content ? <span className="template-has-content">✓</span> : <span className="template-no-content">—</span>}</td>
                      <td className="actions-cell">
                        <button
                          type="button"
                          className="btn-action btn-edit-action"
                          onClick={e => { e.stopPropagation(); navigate(`/hotel/${hotelId}/fulltemplate/${t.id}`); }}
                          title="Edit"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.5 2.5l2 2L6 12l-3 1 1-3 7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="btn-action btn-delete-action"
                          onClick={e => { e.stopPropagation(); handleFullDelete(t); }}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 4h10M6 4V2h4v2M5 4v9h6V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          danger={confirmModal.danger}
          onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
};

export default HotelTemplates;
